"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { playLessonAudioBuffer, stopLessonAudio } from "@lesson/shared/lessonAudio";
import { stripEndToken } from "@lib/agent/conversation/endToken";
import { languageNameForLocale, sttLanguageCode } from "@lib/languageNames";
import { fetchTutorSpeechBuffer } from "@lib/voice/fetchTutorSpeech";
import { useLessonPreferences } from "@components/lesson/LessonPreferencesProvider";
import { useTranslations } from "@i18n/provider";
import type { OverlayMessage } from "../types";
import { ConversationMicButton } from "./ConversationMicButton";

type ConversationInputProps = {
  messages: OverlayMessage[];
  targetLocale: string;
  nativeLocale: string;
  isStreaming: boolean;
  voiceFallback: boolean;
  onVoiceFallback: () => void;
  onSend: (text: string) => void;
  disabled?: boolean;
};

export function ConversationInput({
  messages,
  targetLocale,
  nativeLocale,
  isStreaming,
  voiceFallback,
  onVoiceFallback,
  onSend,
  disabled,
}: ConversationInputProps) {
  const { t } = useTranslations();
  const { maxRecordingSeconds } = useLessonPreferences();
  const [input, setInput] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);
  const [localBannerShown, setLocalBannerShown] = useState(false);
  const bannerShown = voiceFallback || localBannerShown;
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  const lastTtsMessageIdRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const targetLanguage = languageNameForLocale(targetLocale, nativeLocale);

  const triggerVoiceFallback = useCallback(() => {
    if (!bannerShown) {
      setLocalBannerShown(true);
      onVoiceFallback();
    }
  }, [bannerShown, onVoiceFallback]);

  useEffect(() => {
    const lastPersona = [...messages].reverse().find((m) => m.role === "persona" && !m.isStreaming);
    if (!lastPersona || lastPersona.id === lastTtsMessageIdRef.current) return;
    if (!lastPersona.text.trim()) return;

    lastTtsMessageIdRef.current = lastPersona.id;
    const { display } = stripEndToken(lastPersona.text);
    if (!display.trim()) return;

    void (async () => {
      const buffer = await fetchTutorSpeechBuffer(display, { targetLocale });
      if (!buffer) {
        triggerVoiceFallback();
        return;
      }
      try {
        await playLessonAudioBuffer(buffer);
      } catch {
        triggerVoiceFallback();
      }
    })();
  }, [messages, targetLocale, triggerVoiceFallback]);

  // Teardown on unmount (conversation ended or widget dismissed): without
  // this, closing mid-recording leaves the microphone stream live, and
  // closing mid-playback keeps the tutor talking.
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop(); // onstop stops the getUserMedia tracks
      }
      stopLessonAudio();
    };
  }, []);

  async function startRecording() {
    if (disabled || isStreaming || isProcessing) return;
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        // Unmounted mid-recording: mic is released above; don't upload or
        // touch state for a gone component.
        if (cancelledRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mimeType });
        void handleRecordingComplete(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      timerRef.current = setTimeout(() => stopRecording(), maxRecordingSeconds * 1000);
    } catch {
      setMicError(t("speaking.errors.microphoneUnavailable"));
      triggerVoiceFallback();
    }
  }

  function stopRecording() {
    if (timerRef.current) clearTimeout(timerRef.current);
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  }

  async function handleRecordingComplete(blob: Blob) {
    setIsProcessing(true);
    setMicError(null);
    try {
      const file = new File([blob], "recording.webm", { type: blob.type });
      const fd = new FormData();
      fd.append("audio", file);
      fd.append("language", sttLanguageCode(targetLocale));
      const sttRes = await fetch("/api/voice/transcribe", { method: "POST", body: fd });
      if (!sttRes.ok) {
        setMicError(t("speaking.errors.transcribeFailed"));
        triggerVoiceFallback();
        return;
      }
      const { text } = (await sttRes.json()) as { text: string };
      const trimmed = text.trim();
      if (trimmed) {
        onSend(trimmed);
        setInput("");
      }
    } finally {
      setIsProcessing(false);
    }
  }

  function handleSend() {
    const text = input.trim();
    if (!text || isStreaming || disabled) return;
    onSend(text);
    setInput("");
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const inputDisabled = disabled || isStreaming;

  return (
    <div
      className="flex-shrink-0 px-3 pb-3 pt-2"
      style={{ borderTop: "1px solid var(--hairline)" }}
    >
      {bannerShown ? (
        <p className="mb-2 text-xs text-writing-fg" role="alert">
          {t("agent.conversation.voiceFallback")}
        </p>
      ) : null}
      {micError ? (
        <p className="mb-2 text-xs text-writing-fg" role="alert">
          {micError}
        </p>
      ) : null}
      <div className="flex items-end gap-3">
        <ConversationMicButton
          isRecording={isRecording}
          isProcessing={isProcessing}
          disabled={inputDisabled}
          onPointerDown={() => void startRecording()}
          onPointerUp={stopRecording}
        />
        <textarea
          ref={inputRef}
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("agent.conversation.inputPlaceholder", { targetLanguage })}
          disabled={inputDisabled}
          className="inset-field flex-1 resize-none py-2 text-sm"
          style={{ maxHeight: "80px", overflowY: "auto", borderRadius: "12px" }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!input.trim() || inputDisabled}
          aria-label="Send"
          className="mb-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-all hover:scale-105 active:scale-95 disabled:opacity-40"
          style={{
            background: "linear-gradient(135deg, var(--coral-light) 0%, var(--coral) 100%)",
            boxShadow: "var(--shadow-primary)",
          }}
        >
          <svg viewBox="0 0 20 20" fill="white" className="h-4 w-4">
            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
